import { toast } from 'react-toastify'
import { CustomCell, CustomRenderer, GridCellKind, ProvideEditorCallback } from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColorPickerV2 } from '@makyoapp/makyo-ui'

import { IColorCell } from '@/core/models/columns/color'

import { getEditorStyle } from '../helpers/editor-style-helper'

const ICON_FONT_STYLE = '16px Material Symbols Rounded'

/**
 * Props for the CustomColorCell component.
 */
interface CustomColorCellProps {
  readonly type: ColumnTypes
  readonly cell: IColorCell
}

type CustomColorCell = CustomCell<CustomColorCellProps>

/**
 * Editor component for CustomColorCell.
 * It handles the editing logic for color cells.
 */
const ColorEditor: ReturnType<ProvideEditorCallback<CustomColorCell>> = ({ value, onFinishedEditing }) => {
  let cell = value.data.cell
  let cellValue = cell.toString()
  let settings = cell.settings

  const handleEditCell = (newColor?: string) => {
    if (newColor === undefined || newColor === cell.value) {
      onFinishedEditing(undefined)
    } else {
      let updatedCell = cell.toCell(newColor)
      onFinishedEditing({
        ...value,
        data: {
          ...value.data,
          cell: updatedCell,
        },
      })
    }
  }

  return (
    <div className='mk-bg'>
      <ColorPickerV2
        noDropdown
        disableAlpha={!settings.colorTransparent}
        color={cellValue}
        onCancel={() => {
          onFinishedEditing(undefined)
        }}
        onSave={(color) => {
          handleEditCell(color.hex)
        }}
      />
    </div>
  )
}

/**
 * CustomColorRenderer for rendering and managing color cells in a grid.
 */
const CustomColorRenderer: CustomRenderer<CustomColorCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomColorCell =>
    (cell.data as CustomColorCellProps).type === ColumnTypes.Color,
  draw: async (args, cell) => {
    const {
      ctx,
      rect: { x, y, height },
      theme,
      hoverAmount,
      overrideCursor,
    } = args

    const cellValue = cell.data.cell.toString()

    const squareSize = 16
    const spacing = 5
    const leftPadding = 16

    ctx.save()

    ctx.font = theme.fontFamily
    ctx.textBaseline = 'middle'
    const centerY = y + height / 2

    if (!cellValue || cellValue.trim() === '') {
      // Draw empty color cell
      const text = 'Pick a color'
      ctx.fillStyle = 'white'
      ctx.fillRect(x + leftPadding, centerY - squareSize / 2, squareSize, squareSize)
      ctx.strokeStyle = theme.borderColor
      ctx.lineWidth = 1
      ctx.strokeRect(x + leftPadding, centerY - squareSize / 2, squareSize, squareSize)
      ctx.beginPath()
      ctx.moveTo(x + leftPadding, centerY + squareSize / 2)
      ctx.lineTo(x + leftPadding + squareSize, centerY - squareSize / 2)
      ctx.strokeStyle = theme.accentColor
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.fillStyle = theme.textDark
      ctx.fillText(text, x + leftPadding + squareSize + spacing, centerY)
    } else {
      // Draw color cell with selected color
      ctx.fillStyle = cellValue
      ctx.fillRect(x + leftPadding, centerY - squareSize / 2, squareSize, squareSize)
      ctx.strokeStyle = theme.borderColor
      ctx.lineWidth = 1
      ctx.strokeRect(x + leftPadding, centerY - squareSize / 2, squareSize, squareSize)
      ctx.fillStyle = theme.textDark
      ctx.fillText(cellValue, x + leftPadding + squareSize + spacing, centerY)

      // Draw delete icon when hovered
      if (hoverAmount > 0 && overrideCursor) {
        overrideCursor('pointer')
        ctx.font = ICON_FONT_STYLE
        const deleteIcon = 'delete'
        const textMetrics = ctx.measureText(cellValue)
        const iconX = x + textMetrics.width
        ctx.fillStyle = theme.textDark
        ctx.fillText(deleteIcon, iconX, centerY)
      }
    }

    ctx.restore()
    return true
  },
  measure: (ctx, cell: any, theme) => {
    const color = cell.data.cell.toString() || 'Pick a color'
    const squareSize = 16
    const leftPadding = 16
    const spacing = 5
    ctx.font = theme.fontFamily
    const textMetrics = ctx.measureText(color)
    return textMetrics.width + leftPadding + squareSize + spacing + 40
  },
  onClick: (args: any): CustomColorCell | undefined => {
    const colorCell = args.cell.data.cell
    const clickAction = handleCellClick(args)

    switch (clickAction) {
      case 'delete':
        args.preventDefault()
        const dataEditorRef = args.cell.data.extra.dataEditorRef.current
        dataEditorRef.emit('delete')
        break
      case 'open_editor':
        return undefined
      default:
        args.preventDefault()
    }

    return {
      ...args.cell,
      data: {
        ...args.cell.data,
        cell: colorCell,
      },
    }
  },
  provideEditor: () => {
    const style = { minWidth: '600px', width: '600px' }
    const customEditorHeight = getEditorStyle(530, true)
    return {
      editor: ColorEditor,
      disablePadding: true,
      disableStyling: false,
      styleOverride: { ...style, ...customEditorHeight },
    }
  },
  onPaste: (v, cellData) => ({
    ...cellData,
    cell: cellData.cell.toCell(v) as IColorCell,
  }),
  onDelete(cellData) {
    toast.success('Color deleted')
    return {
      ...cellData,
      data: {
        ...cellData.data,
        cell: cellData.data.cell.toCell(null) as IColorCell,
      },
    }
  },
  needsHover: true,
}

/**
 * Determines which action to take based on where the user clicked in the cell.
 *
 * @param {Object} args - The click event arguments
 * @returns {string} The action to be performed ('open_editor' or 'delete')
 */
const handleCellClick = (args: any): string | undefined => {
  const { cell, posX: clickX, theme } = args

  const color = cell.data.cell.toString()
  const isEmptyColor = !color || color.trim() === ''

  if (isEmptyColor) {
    return 'open_editor'
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) return 'open_editor'

  const squareSize = 16
  const spacing = 5
  const leftPadding = 16

  ctx.save()

  ctx.font = theme.fontFamily
  ctx.textBaseline = 'middle'

  // color box x position
  const boxX = Math.floor(leftPadding + squareSize)

  const text = color
  ctx.fillStyle = theme.textDark

  const textMetrics = ctx.measureText(text)

  // text x position
  const textX = Math.floor(boxX + spacing + textMetrics.width + 2 * textMetrics.fontBoundingBoxAscent)

  ctx.font = ICON_FONT_STYLE
  const deleteIcon = 'delete'
  const iconMetrics = ctx.measureText(deleteIcon)

  // delete icon x position
  const iconX = Math.floor(textX + iconMetrics.width)

  ctx.restore()

  if (clickX > leftPadding - 2 && clickX < textX + 2) {
    return 'open_editor'
  }

  if (clickX > iconX - 2 && clickX < Math.floor(iconX + iconMetrics.width + 2)) {
    return 'delete'
  }
}

export default CustomColorRenderer
